import { SIDENAV_TOKENS } from '@app/components/layout/sidenav/sidenav-tokens';
import { ActionButton } from '@knocklabs/client';
import { Box, ListItem, ListItemButton, Typography } from '@mui/material';
import {
  NotificationSeverity,
  getNotificationSeverityColor,
} from '../../shared/notification-severity';
import { NotificationFeedItemActions } from './NotificationFeedItemActions';
import { NotificationFeedItemArchiveButton } from './NotificationFeedItemArchiveButton';

export type NotificationFeedItemViewProps = {
  /** Product-area label (workflow category); shown above the title. */
  eyebrow?: string | null;
  title: string;
  /** Knock-rendered HTML body; omitted when absent. */
  bodyHtml?: string | null;
  /** Relative or absolute time label already formatted for display. */
  timeLabel: string;
  unread: boolean;
  /** Reserved for actor avatar; not rendered for now. */
  avatarName?: string | null;
  /** Reserved for actor avatar; not rendered for now. */
  avatarSrc?: string | null;
  buttons?: ActionButton[];
  /**
   * When true, the row is clickable and navigates via `onClick`.
   * When false, the row is not interactive (no silent mark-as-read); use buttons / archive instead.
   */
  hasAction?: boolean;
  severity?: NotificationSeverity;
  /** Primary navigate handler; only wired when `hasAction` is true. */
  onClick?: () => void;
  onArchive?: () => void;
  onButtonClick?: (button: ActionButton) => void;
};

/**
 * Presentational row for one in-app notification in the MUI feed list.
 * Actor avatars are intentionally hidden for now.
 * Severity uses a left ribbon (full opacity unread, muted when read).
 * Primary destination uses row click; secondary actions use buttons under the body.
 */
export function NotificationFeedItemView({
  eyebrow = null,
  title,
  bodyHtml = null,
  timeLabel,
  unread,
  buttons = [],
  hasAction = true,
  severity = 'info',
  onClick,
  onArchive,
  onButtonClick,
}: NotificationFeedItemViewProps) {
  const { activeAccentWidth, activeAccentInsetX, activeAccentInsetY, activeAccentRadius } =
    SIDENAV_TOKENS;

  const rowSx = {
    position: 'relative' as const,
    display: 'flex' as const,
    width: '100%',
    alignItems: 'flex-start' as const,
    // Left padding leaves room for the severity ribbon in the gutter.
    pl: 2,
    pr: 1,
    py: 1.25,
    gap: 1,
    bgcolor: unread ? 'grey.50' : 'transparent',
    cursor: hasAction ? 'pointer' : 'default',
    '&::before': {
      content: '""',
      position: 'absolute' as const,
      top: activeAccentInsetY,
      bottom: activeAccentInsetY,
      left: activeAccentInsetX,
      width: activeAccentWidth,
      borderRadius: `${activeAccentRadius}px`,
      bgcolor: getNotificationSeverityColor(severity),
      opacity: unread ? 1 : 0.35,
      pointerEvents: 'none' as const,
    },
  };

  const content = (
    <Box sx={{ minWidth: 0, flex: 1 }}>
      {/* Meta row: area · time, with time/archive trailing when archive is present. */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          minWidth: 0,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            minWidth: 0,
            flex: 1,
          }}
        >
          {eyebrow ? (
            <Typography variant="caption" color="text.secondary" noWrap sx={{ minWidth: 0 }}>
              {eyebrow}
            </Typography>
          ) : null}
          {eyebrow ? (
            <Typography variant="caption" color="text.disabled" aria-hidden>
              ·
            </Typography>
          ) : null}
          <Typography
            variant="caption"
            color="text.disabled"
            sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
          >
            {timeLabel}
          </Typography>
        </Box>
        {onArchive ? <NotificationFeedItemArchiveButton onArchive={onArchive} /> : null}
      </Box>
      <Typography variant="body2" sx={{ fontWeight: unread ? 500 : 400, wordBreak: 'break-word' }}>
        {title}
      </Typography>
      {bodyHtml ? (
        <Box
          sx={{
            typography: 'caption',
            color: 'text.secondary',
            mt: 0.25,
            wordBreak: 'break-word',
            '& p': { m: 0 },
            '& a': { color: 'primary.main' },
          }}
          dangerouslySetInnerHTML={{ __html: bodyHtml }}
        />
      ) : null}
      {onButtonClick ? (
        <NotificationFeedItemActions buttons={buttons} onButtonClick={onButtonClick} />
      ) : null}
    </Box>
  );

  if (hasAction && onClick) {
    return (
      <ListItemButton disableGutters disableRipple onClick={onClick} sx={rowSx}>
        {content}
      </ListItemButton>
    );
  }

  return (
    <ListItem disableGutters sx={rowSx}>
      {content}
    </ListItem>
  );
}
